import { oneToOneMapping } from 'common/utils';
import { humanizeRole } from 'common/helpers/humanize';
import { getHierarchyConfigDisplayName, getHierarchyLocationDisplayName } from 'common/utils/hierarchyConfigDisplayName';
import { TYPE_LABEL, TYPE_LABEL_I18N_KEY } from './annotationTypes';

const ALARM_TYPE_PHRASE_KEYS = {
  'vibration envelope alarm': 'activity.activityFormat.titles.vibrationEnvelopeAlarm',
  'current envelope alarm': 'activity.activityFormat.titles.currentEnvelopeAlarm',
  'vibration warning alarm': 'activity.activityFormat.titles.vibrationWarningAlarm',
  'vibration critical alarm': 'activity.activityFormat.titles.vibrationCriticalAlarm',
  'demod vibration warning alarm': 'activity.activityFormat.titles.demodVibrationWarningAlarm',
  'demod vibration critical alarm': 'activity.activityFormat.titles.demodVibrationCriticalAlarm',
  'vibration acceleration warning alarm': 'activity.activityFormat.titles.vibrationAccelerationWarningAlarm',
  'vibration acceleration critical alarm': 'activity.activityFormat.titles.vibrationAccelerationCriticalAlarm',
  'health score warning': 'activity.activityFormat.titles.healthScoreWarning',
  'health score critical': 'activity.activityFormat.titles.healthScoreCritical',
};

// Generic overall-alarm sentence, e.g. "Motor-12 has Rms Velocity alarm"
// (built by events_microservice's GENERIC_TAG_EVENT_TITLE = "{machine_name} has {parameter_type} alarm").
// Distinct from DESCRIPTION_PATTERNS below (which all require "machine has a ... on the ...")
// so it's checked separately rather than added to that array.
const PARAMETER_ALARM_PATTERN = /^(.+?)\s+has\s+(.+?)\s+alarm$/i;

const DESCRIPTION_PATTERNS = [
  {
    regex: /^(.+?)\s+machine has a\s+(.+?)\s+on the\s+(.+?)\s+at\s+(.+?)\s+in\s+(.+?)\s+direction$/i,
    key: 'annotations.descriptions.machineAlarmOnLocationAtComponentInDirection',
    defaults: '{{machineName}} machine has a {{alarmType}} on the {{location}} at {{componentName}} in {{direction}} direction',
  },
  {
    regex: /^(.+?)\s+machine has a\s+(.+?)\s+on the\s+(.+?)\s+in\s+(.+?)\s+direction$/i,
    key: 'annotations.descriptions.machineAlarmOnLocationInDirection',
    defaults: '{{machineName}} machine has a {{alarmType}} on the {{location}} in {{direction}} direction',
  },
  {
    regex: /^(.+?)\s+machine has a\s+(.+?)\s+on the\s+(.+?)\s+at\s+(.+?)$/i,
    key: 'annotations.descriptions.machineAlarmOnLocationAtComponent',
    defaults: '{{machineName}} machine has a {{alarmType}} on the {{location}} at {{componentName}}',
  },
  {
    regex: /^(.+?)\s+machine has a\s+(.+?)\s+on the\s+(.+?)$/i,
    key: 'annotations.descriptions.machineAlarmOnLocation',
    defaults: '{{machineName}} machine has a {{alarmType}} on the {{location}}',
  },
];

function normalizeToken(value) {
  return String(value || '').trim().toLowerCase();
}

export function getAnnotationTypeLabel(t, type) {
  if (!type) return '';
  return t(TYPE_LABEL_I18N_KEY[type] || '', { defaultValue: TYPE_LABEL[type] || type });
}

export function getAnnotationSeverityLabel(t, severity) {
  if (!severity) return '';
  const key = normalizeToken(severity);
  return t(`annotations.severity.${key}`, { defaultValue: severity });
}

export function getAnnotationAlarmStateLabel(t, state) {
  const key = normalizeToken(state || 'unknown').replace(/\s+/g, '_');
  return t(`annotations.alarmStates.${key}`, {
    defaultValue: String(state || 'unknown').replace(/_/g, ' '),
  });
}

export function getAnnotationUserLabel(t, annotation) {
  if (!annotation) return '-';
  const direct = annotation.created_by_name || annotation.created_by;
  if (!direct) return '-';
  const value = String(direct);
  if (normalizeToken(value) === 'system') {
    // System-generated: no human author, so nothing to attribute a role to.
    return t('annotations.user.system', { defaultValue: 'System' });
  }
  const atIndex = value.indexOf('@');
  const name = atIndex > 0 ? value.slice(0, atIndex) : value;
  // `created_by_role` is optional by contract (null for system annotations and
  // whenever the backend's per-account role lookup can't resolve one), so fall
  // back to the bare name instead of rendering empty brackets.
  const role = annotation.created_by_role
    ? humanizeRole(annotation.created_by_role, t)
    : '';
  return role ? `${name} (${role})` : name;
}

function getDirectionLabel(t, direction) {
  const key = normalizeToken(direction);
  return t(`common.directions.${key}`, { defaultValue: direction });
}

function getAlarmTypeLabel(t, alarmTypePhrase) {
  const normalized = normalizeToken(alarmTypePhrase);
  const i18nKey = ALARM_TYPE_PHRASE_KEYS[normalized];
  if (i18nKey) {
    return t(i18nKey, { defaultValue: alarmTypePhrase });
  }
  return alarmTypePhrase;
}

// Reuses the existing eventDefinitions.parameters.<snake_case> catalog (already translated
// for the event-definition UI) rather than introducing a parallel set of keys — the backend's
// presentable-case phrase (e.g. "Rms Velocity") maps 1:1 back to its TriggerParameterEnum value
// (e.g. "rms_velocity") once lower-cased and underscored.
function getParameterTypeLabel(t, parameterTypePhrase) {
  const snakeCase = normalizeToken(parameterTypePhrase).replace(/\s+/g, '_');
  return t(`eventDefinitions.parameters.${snakeCase}`, { defaultValue: parameterTypePhrase });
}

function buildAlarmTypePhraseMap(t) {
  const map = {};
  Object.entries(ALARM_TYPE_PHRASE_KEYS).forEach(([englishPhrase, i18nKey]) => {
    map[englishPhrase] = t(i18nKey, { defaultValue: englishPhrase });
  });
  return map;
}

function localizeDescriptionFallback(t, description) {
  if (!description) return description;
  let result = getHierarchyConfigDisplayName(t, description, { shorten: false });
  result = oneToOneMapping(buildAlarmTypePhraseMap(t), result);
  return result;
}

/**
 * Localized annotation description for list items, tooltips, and modals.
 * Parses common backend alarm sentence templates; falls back to phrase replacement.
 */
export function getAnnotationDescriptionDisplay(t, annotation) {
  const description = annotation && annotation.description;
  if (!description) return '';

  const parameterAlarmMatch = description.match(PARAMETER_ALARM_PATTERN);
  if (parameterAlarmMatch) {
    const [, machineName, parameterTypePhrase] = parameterAlarmMatch;
    return t('annotations.descriptions.machineHasParameterAlarm', {
      machineName: machineName.trim(),
      parameterType: getParameterTypeLabel(t, parameterTypePhrase.trim()),
      defaultValue: '{{machineName}} has {{parameterType}} alarm',
    });
  }

  for (let i = 0; i < DESCRIPTION_PATTERNS.length; i += 1) {
    const { regex, key, defaults } = DESCRIPTION_PATTERNS[i];
    const match = description.match(regex);
    if (match) {
      const [, machineName, alarmTypePhrase, location, maybeFourth, maybeFifth] = match;
      const hasDirection = key.includes('InDirection');
      const hasComponent = key.includes('AtComponent');

      const params = {
        machineName: machineName.trim(),
        alarmType: getAlarmTypeLabel(t, alarmTypePhrase),
        location: getHierarchyLocationDisplayName(t, location.trim()),
        defaultValue: defaults,
      };

      if (hasComponent) {
        params.componentName = maybeFourth.trim();
      }
      if (hasDirection) {
        params.direction = getDirectionLabel(
          t,
          hasComponent ? maybeFifth : maybeFourth
        );
      }

      return t(key, params);
    }
  }

  return localizeDescriptionFallback(t, description);
}

export function getAnnotationTitleDisplay(t, annotation) {
  const baseMetadata = annotation.base_metadata || {};
  const userMetadata = annotation.user_metadata || {};

  if (userMetadata.title) return userMetadata.title;
  if (annotation.type === 'task' && baseMetadata.task_name) return baseMetadata.task_name;
  if (annotation.type === 'repair' && baseMetadata.repair_name) return baseMetadata.repair_name;
  if (baseMetadata.title) return baseMetadata.title;
  if (annotation.description) return getAnnotationDescriptionDisplay(t, annotation);

  return getAnnotationTypeLabel(t, annotation.type) || t('annotations.common.annotationFallbackTitle', {
    defaultValue: 'Annotation',
  });
}
